import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient, { authAPI, setAuthToken } from '../utils/api';
import { Users, UserPlus, Activity, Clock, Target, ArrowLeft, Trash2, LogOut, Download, Search, ArrowUp, ArrowDown, HardDrive, RefreshCw, CheckCircle2, ShieldQuestion, XCircle } from 'lucide-react';
import SupportInbox from '../components/admin/SupportInbox';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className={`p-6 rounded-xl bg-white/80 backdrop-blur-sm border border-stone-200 shadow-sm`} data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-sm font-medium text-stone-600">{label}</span>
    </div>
    <div className="text-3xl font-bold text-stone-900">{value}</div>
  </div>
);

const Admin = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [query, setQuery] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [totalCount, setTotalCount] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [cacheBusy, setCacheBusy] = useState(false);
  const [onlineNow, setOnlineNow] = useState(0);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingBusy, setPendingBusy] = useState({});

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll online count every 10s
  useEffect(() => {
    if (!me?.is_admin) return;
    let cancelled = false;
    const fetchOnline = async () => {
      try {
        const r = await apiClient.get('/chat/admin/online-count');
        if (!cancelled) setOnlineNow(r.data?.unique_users || 0);
      } catch (_) { /* ignore */ }
    };
    fetchOnline();
    const id = setInterval(fetchOnline, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [me?.is_admin]);

  // Re-fetch users on query/sort change (debounced for query)
  useEffect(() => {
    if (!me?.is_admin) return;
    const id = setTimeout(() => {
      fetchUsers();
    }, query ? 300 : 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sortField, sortOrder, me]);

  const bootstrap = async () => {
    try {
      const meRes = await authAPI.getMe();
      setMe(meRes.data);
      if (!meRes.data.is_admin) {
        setAuthError('Bu sayfaya erişim yetkiniz yok.');
        setLoading(false);
        return;
      }
      await fetchStats();
      await fetchCacheInfo();
      await fetchPending();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        toast.error('Lütfen tekrar giriş yapın');
        navigate('/auth');
      } else {
        setAuthError('Yetki doğrulanamadı');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = React.useCallback(async () => {
    const res = await apiClient.get('/admin/stats');
    setStats(res.data);
  }, []);

  const fetchUsers = React.useCallback(async () => {
    try {
      setUsersLoading(true);
      const params = { limit: 100, skip: 0, sort: sortField, order: sortOrder };
      if (query.trim()) params.q = query.trim();
      const res = await apiClient.get('/admin/users', { params });
      setUsers(res.data.users || []);
      setTotalCount(res.data.total || 0);
    } catch (e) {
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setUsersLoading(false);
    }
  }, [sortField, sortOrder, query]);

  const toggleSort = React.useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('desc');
      return field;
    });
  }, []);

  const handleExportCsv = async () => {
    try {
      const params = { responseType: 'blob' };
      if (query.trim()) params.params = { q: query.trim() };
      const res = await apiClient.get('/admin/users.csv', params);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      a.download = `hafizim-users-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV indirildi');
    } catch (e) {
      toast.error('CSV indirilemedi');
    }
  };

  const fetchCacheInfo = async () => {
    try {
      const res = await apiClient.get('/admin/tts-cache');
      setCacheInfo(res.data);
    } catch (e) {
      // silently ignore — endpoint may be missing on older deploys
    }
  };

  const fetchPending = async () => {
    try {
      const res = await apiClient.get('/admin/users', { params: { pending: true, limit: 100 } });
      setPendingUsers(res.data.users || []);
    } catch (e) {
      /* ignore */
    }
  };

  const approveUser = async (uid) => {
    try {
      setPendingBusy((m) => ({ ...m, [uid]: true }));
      await apiClient.post(`/admin/users/${uid}/approve`);
      toast.success('Kullanıcı onaylandı, e-posta gönderildi');
      setPendingUsers((prev) => prev.filter((u) => u.id !== uid));
      fetchStats();
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Onaylanamadı');
    } finally {
      setPendingBusy((m) => { const n = { ...m }; delete n[uid]; return n; });
    }
  };

  const rejectUser = async (uid, email) => {
    if (!window.confirm(`${email} reddedilecek ve hesap silinecek. Devam edilsin mi?`)) return;
    try {
      setPendingBusy((m) => ({ ...m, [uid]: true }));
      await apiClient.delete(`/admin/users/${uid}`);
      toast.success('Kullanıcı reddedildi ve silindi');
      setPendingUsers((prev) => prev.filter((u) => u.id !== uid));
      fetchStats();
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reddedilemedi');
    } finally {
      setPendingBusy((m) => { const n = { ...m }; delete n[uid]; return n; });
    }
  };

  const clearCache = async (onlyOa) => {
    const msg = onlyOa
      ? 'Eski OpenAI ses dosyaları silinecek. Devam edilsin mi?'
      : 'TÜM TTS cache (ElevenLabs + OpenAI) silinecek. Devam edilsin mi?';
    if (!window.confirm(msg)) return;
    try {
      setCacheBusy(true);
      const params = onlyOa ? { only: 'oa' } : {};
      const res = await apiClient.post('/admin/tts-cache/clear', null, { params });
      toast.success(`${res.data.removed} dosya silindi`);
      await fetchCacheInfo();
    } catch (e) {
      toast.error('Cache silinemedi');
    } finally {
      setCacheBusy(false);
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`${email} kullanıcısını silmek istediğinizden emin misiniz?`)) return;
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      toast.success('Kullanıcı silindi');
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silme başarısız');
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="admin-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 px-6" data-testid="admin-forbidden">
        <h2 className="text-2xl font-bold text-stone-900 mb-3">Erişim Engellendi</h2>
        <p className="text-stone-600 mb-6">{authError}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800"
          data-testid="admin-back-to-dashboard"
        >
          Ana Sayfaya Dön
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-page">
      {/* Header */}
      <header className="px-6 py-4 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-stone-600 hover:text-stone-900"
              data-testid="admin-back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Ana Sayfa</span>
            </button>
            <h1 className="text-2xl font-bold font-elegant text-stone-900">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-stone-600">{me?.email}</span>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg hover:bg-stone-100"
              data-testid="admin-logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <StatCard icon={Users} label="Toplam Kullanıcı" value={stats.total_users} color="bg-emerald-700" />
            <StatCard icon={UserPlus} label="Yeni 7 Gün" value={stats.new_users_last_7d} color="bg-amber-600" />
            <StatCard icon={Activity} label="Son 24s Aktif" value={stats.active_users_last_24h} color="bg-teal-600" />
            <StatCard icon={Clock} label="Aktif Dakika" value={stats.total_active_minutes} color="bg-rose-600" />
            <StatCard icon={Target} label="Toplam Deneme" value={stats.total_attempts} color="bg-purple-600" />
            <StatCard icon={Target} label="Başarılı" value={stats.successful_attempts} color="bg-green-600" />
            <StatCard icon={Activity} label="🟢 Şu An Online" value={onlineNow} color="bg-emerald-600" />
          </div>
        )}

        {/* TTS Cache management */}
        {cacheInfo && (
          <div className="mb-8 p-5 bg-white rounded-xl border border-stone-200 shadow-sm" data-testid="admin-tts-cache">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-600">
                  <HardDrive className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-stone-900 font-elegant">TTS Ses Cache</h3>
                  <p className="text-xs text-stone-500">
                    {cacheInfo.file_count} dosya · {cacheInfo.total_mb} MB
                    {cacheInfo.by_provider?.el && ` · ElevenLabs: ${cacheInfo.by_provider.el.count}`}
                    {cacheInfo.by_provider?.oa && ` · OpenAI: ${cacheInfo.by_provider.oa.count}`}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={fetchCacheInfo}
                  disabled={cacheBusy}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700"
                  data-testid="admin-cache-refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                  Yenile
                </button>
                <button
                  onClick={() => clearCache(true)}
                  disabled={cacheBusy || !(cacheInfo.by_provider?.oa?.count)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="admin-cache-clear-oa"
                  title="Yalnızca eski OpenAI ses dosyalarını siler — ElevenLabs cache korunur"
                >
                  <Trash2 className="w-4 h-4" />
                  Eski OpenAI Dosyalarını Sil
                </button>
                <button
                  onClick={() => clearCache(false)}
                  disabled={cacheBusy || cacheInfo.file_count === 0}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="admin-cache-clear-all"
                >
                  <Trash2 className="w-4 h-4" />
                  Tüm Cache&apos;i Sil
                </button>
              </div>
            </div>
            {cacheInfo.by_provider?.oa?.count > 0 && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                💡 {cacheInfo.by_provider.oa.count} adet eski OpenAI dosyası var. ElevenLabs aktif olduğu için bunlar kullanılmıyor ama silmek kaliteli sesin yeniden üretildiğinden emin olmanızı sağlar.
              </div>
            )}
          </div>
        )}

        {/* Pending approvals (admin must approve new accounts) */}
        <div className="mb-8 bg-white border-2 border-amber-200 rounded-xl shadow-sm overflow-hidden" data-testid="admin-pending-approvals">
          <div className="px-6 py-4 border-b border-amber-200 bg-amber-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500">
                <ShieldQuestion className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-stone-900 font-elegant">Onay Bekleyen Üyelikler</h2>
                <p className="text-xs text-stone-600">
                  {pendingUsers.length === 0
                    ? 'Bekleyen başvuru yok.'
                    : `${pendingUsers.length} kullanıcı admin onayı bekliyor.`}
                </p>
              </div>
            </div>
            <button
              onClick={fetchPending}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
              data-testid="admin-pending-refresh"
            >
              <RefreshCw className="w-4 h-4" />
              Yenile
            </button>
          </div>
          {pendingUsers.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-600">
                  <tr>
                    <th className="px-4 py-3 text-left">Ad</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Yaş Grubu</th>
                    <th className="px-4 py-3 text-left">Kayıt</th>
                    <th className="px-4 py-3 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map((u) => (
                    <tr key={u.id} className="border-t border-stone-100" data-testid={`admin-pending-row-${u.id}`}>
                      <td className="px-4 py-3 font-medium text-stone-900">{u.name}</td>
                      <td className="px-4 py-3 text-stone-600">{u.email}</td>
                      <td className="px-4 py-3 text-stone-600">{u.age_group}</td>
                      <td className="px-4 py-3 text-stone-600">{formatDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => approveUser(u.id)}
                            disabled={!!pendingBusy[u.id]}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                            data-testid={`admin-approve-user-${u.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" /> Onayla
                          </button>
                          <button
                            onClick={() => rejectUser(u.id, u.email)}
                            disabled={!!pendingBusy[u.id]}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-stone-200 text-stone-700 hover:bg-stone-300 disabled:opacity-50"
                            data-testid={`admin-reject-user-${u.id}`}
                          >
                            <XCircle className="w-4 h-4" /> Reddet
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Support Inbox (KVKK requests + contact form) */}
        <SupportInbox />

        {/* Users table */}
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm" data-testid="admin-users-table">
          <div className="px-6 py-4 border-b border-stone-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div className="flex items-center gap-3 flex-1">
              <h2 className="text-lg font-bold text-stone-900 font-elegant whitespace-nowrap">Kullanıcılar</h2>
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ad veya email ile ara..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
                  data-testid="admin-search-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-500">Toplam: {totalCount}</span>
              <button
                onClick={handleExportCsv}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg bg-emerald-700 text-white hover:bg-emerald-800"
                data-testid="admin-export-csv"
              >
                <Download className="w-4 h-4" />
                CSV İndir
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-600">
                <tr>
                  {[
                    { key: 'name', label: 'Ad', sortable: true },
                    { key: 'email', label: 'Email', sortable: true },
                    { key: 'age_group', label: 'Yaş Grubu', sortable: false },
                    { key: 'created_at', label: 'Kayıt', sortable: true },
                    { key: 'last_login', label: 'Son Giriş', sortable: true },
                  ].map(({ key, label, sortable }) => (
                    <th
                      key={key}
                      onClick={sortable ? () => toggleSort(key) : undefined}
                      className={`px-4 py-3 text-left ${sortable ? 'cursor-pointer hover:bg-stone-100 select-none' : ''}`}
                      data-testid={`admin-sort-${key}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        {sortable && sortField === key && (
                          sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right">Aktif Dak.</th>
                  <th className="px-4 py-3 text-right">Deneme</th>
                  <th className="px-4 py-3 text-right">Elif-Ba</th>
                  <th className="px-4 py-3 text-right">Streak</th>
                  <th className="px-4 py-3 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {usersLoading && (
                  <tr>
                    <td colSpan="10" className="px-4 py-6 text-center text-stone-500" data-testid="admin-users-loading">
                      Yükleniyor...
                    </td>
                  </tr>
                )}
                {!usersLoading && users.map((u) => (
                  <tr key={u.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium text-stone-900">
                      {u.name}
                      {u.is_admin && (
                        <span className="ml-2 px-2 py-0.5 text-[10px] font-bold bg-emerald-700 text-white rounded-full uppercase">Admin</span>
                      )}
                      {!u.is_admin && !u.is_approved && (
                        <span className="ml-2 px-2 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full uppercase">Bekliyor</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-stone-600">{u.email}</td>
                    <td className="px-4 py-3 text-stone-600">{u.age_group}</td>
                    <td className="px-4 py-3 text-stone-600">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-stone-600">{formatDate(u.last_login)}</td>
                    <td className="px-4 py-3 text-right text-stone-700">{u.active_minutes}</td>
                    <td className="px-4 py-3 text-right text-stone-700">{u.total_attempts}</td>
                    <td className="px-4 py-3 text-right text-stone-700">{u.elifba_completed}</td>
                    <td className="px-4 py-3 text-right text-stone-700">{u.streak_days}</td>
                    <td className="px-4 py-3 text-right">
                      {!u.is_admin && (
                        <div className="inline-flex gap-1">
                          {!u.is_approved && (
                            <button
                              onClick={() => approveUser(u.id)}
                              disabled={!!pendingBusy[u.id]}
                              className="p-1.5 rounded-md text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                              data-testid={`admin-approve-inline-${u.id}`}
                              title="Onayla"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(u.id, u.email)}
                            className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50"
                            data-testid={`admin-delete-user-${u.id}`}
                            title="Kullanıcıyı sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!usersLoading && users.length === 0 && (
                  <tr>
                    <td colSpan="10" className="px-4 py-12 text-center text-stone-500" data-testid="admin-users-empty">
                      {query ? `"${query}" için sonuç bulunamadı` : 'Henüz kullanıcı yok'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
